import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FullProjectHash } from "../../../_models/project.interface";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import {
  faBan,
  faCheckCircle,
  faGripVertical,
  faHandPaper,
  faMobileAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { CdkDragDrop, DragDropModule, moveItemInArray } from "@angular/cdk/drag-drop";
import { concat, last } from "rxjs";
import { TooltipModule } from "@cloudfactorydk/ng2-tooltip-directive";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { AddNewTaskModalComponent } from "../modal-components/add-new-task-modal/add-new-task-modal.component";
import { AddNewPriorityModalComponent } from "../modal-components/add-new-priority-modal/add-new-priority-modal.component";
import { AddNewStatusModalComponent } from "../modal-components/add-new-status-modal/add-new-status-modal.component";
import { FullTaskHash } from "../../../_models/task.interface";
import { FullStatusHash } from "../../../_models/status.interface";
import { FullPriorityHash } from "../../../_models/priority.interface";
import { EditPriorityModalComponent } from "../modal-components/edit-priority-modal/edit-priority-modal.component";
import { EditStatusModalComponent } from "../modal-components/edit-status-modal/edit-status-modal.component";
import { EditTaskModalComponent } from "../modal-components/edit-task-modal/edit-task-modal.component";

import { ProjectService } from "../../../_services";

@Component({
  selector: "hawk-project-edit-ticket-attributes",
  imports: [
    FaIconComponent,
    TooltipModule,
    DragDropModule,
    AddNewTaskModalComponent,
    AddNewPriorityModalComponent,
    AddNewStatusModalComponent,
    EditPriorityModalComponent,
    EditStatusModalComponent,
    EditTaskModalComponent,
  ],
  templateUrl: "./project-edit-ticket-attributes.component.html",
  standalone: true,
  styleUrls: ["./project-edit-ticket-attributes.component.scss"],
  host: {
    class: "flex flex-col gap-4",
  },
})
export class ProjectEditTicketAttributesComponent {
  protected readonly faTrash = faTrash;
  protected readonly faCheckCircle = faCheckCircle;
  protected readonly faHandPaper = faHandPaper;
  protected readonly faBan = faBan;
  protected readonly faMobileAlt = faMobileAlt;
  protected readonly faGripVertical = faGripVertical;

  @Input() project: FullProjectHash;
  @Output() updateProjectData: EventEmitter<FullProjectHash> = new EventEmitter<FullProjectHash>();

  editTaskForm: UntypedFormGroup;
  editStatusForm: UntypedFormGroup;
  editPriorityForm: UntypedFormGroup;

  addTaskForm: UntypedFormGroup;
  addStatusForm: UntypedFormGroup;
  addPriorityForm?: UntypedFormGroup;

  constructor(private projectService: ProjectService) {}

  openAddTaskForm(): void {
    this.addTaskForm = this.getTaskForm();
  }

  openEditTaskForm(task: FullTaskHash): void {
    this.editTaskForm = this.getTaskForm(task);
  }

  getTaskForm(template?: FullTaskHash): UntypedFormGroup {
    const task_form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      name: new UntypedFormControl({ value: "" }, Validators.required),
      user_ids: new UntypedFormControl({ value: [] }),
      use_deadline: new UntypedFormControl({ value: false }),
      deadline_days: new UntypedFormControl({ value: 0 }),
      deadline_hours: new UntypedFormControl({ value: 0 }),
      deadline_minutes: new UntypedFormControl({ value: 0 }),
      deadline_user_ids: new UntypedFormControl({ value: [] }),
      project_id: new UntypedFormControl({ value: this.project.id }, Validators.required),
      mail: new UntypedFormControl({ value: true }),
      sms: new UntypedFormControl({ value: false }),
    });

    task_form.controls["id"].setValue(template?.id || null);
    task_form.controls["name"].setValue(template?.name || "");
    task_form.controls["user_ids"].setValue([]); // TODO add template init
    task_form.controls["use_deadline"].setValue(template ? template.use_deadline : false);
    task_form.controls["deadline_days"].setValue(template?.deadline_days || 0);
    task_form.controls["deadline_hours"].setValue(template?.deadline_hours || 0);
    task_form.controls["deadline_minutes"].setValue(template?.deadline_minutes || 0);
    task_form.controls["deadline_user_ids"].setValue([]); // TODO add template init
    task_form.controls["project_id"].setValue(this.project.id);
    task_form.controls["mail"].setValue(template ? template.mail : true);
    task_form.controls["sms"].setValue(template ? template.sms : false);

    task_form.updateValueAndValidity();

    return task_form;
  }

  openAddStatusForm(): void {
    this.addStatusForm = this.getStatusForm();
  }

  openEditStatusForm(status: FullStatusHash): void {
    this.editStatusForm = this.getStatusForm(status);
  }

  getStatusForm(template?: FullStatusHash): UntypedFormGroup {
    const status_form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      name: new UntypedFormControl({ value: "" }, Validators.required),
      task_ids: new UntypedFormControl({ value: [] }),
      closed: new UntypedFormControl({ value: false }),
      canceled: new UntypedFormControl({ value: false }),
      report_closed: new UntypedFormControl({ value: false }),
      color: new UntypedFormControl({ value: "" }),
      fixer_forbidden: new UntypedFormControl({ value: false }),
      visible_app: new UntypedFormControl({ value: false }),
      fallback_status: new UntypedFormControl({ value: false }),
      unplanned: new UntypedFormControl({ value: false }),
      mail_template_id: new UntypedFormControl({ value: null }),
      email: new UntypedFormControl({ value: "" }),
      project_id: new UntypedFormControl({ value: this.project.id }, Validators.required),
    });

    status_form.controls["id"].setValue(template?.id || null);
    status_form.controls["name"].setValue(template?.name || "");
    status_form.controls["task_ids"].setValue([]); // TODO add template init
    status_form.controls["closed"].setValue(template ? template.closed : false);
    status_form.controls["canceled"].setValue(template ? template.canceled : false);
    status_form.controls["report_closed"].setValue(template ? template.report_closed : false);
    status_form.controls["color"].setValue(template?.color || "#ffffff");
    status_form.controls["fixer_forbidden"].setValue(template ? template.fixer_forbidden : false);
    status_form.controls["visible_app"].setValue(template ? template.visible_app : false);
    status_form.controls["fallback_status"].setValue(template ? template.fallback_status : false);
    status_form.controls["unplanned"].setValue(template ? template.unplanned : false);
    status_form.controls["mail_template_id"].setValue(template?.mail_template?.id || null);
    status_form.controls["email"].setValue(template?.email || "");
    status_form.controls["project_id"].setValue(this.project.id);

    status_form.updateValueAndValidity();

    // Auto-Set nur beim Anlegen (kein template): Name "1.x"/"2.x" belegt das
    // unplanned-Flag automatisch vor — solange der User die Checkbox nicht selbst
    // angefasst hat. Eigene programmatische Sets laufen mit emitEvent:false, damit
    // sie nicht fälschlich als "manuell angefasst" gelten.
    if (!template) {
      let unplannedTouched = false;
      const unplannedCtrl = status_form.controls["unplanned"];
      unplannedCtrl.valueChanges.subscribe(() => {
        unplannedTouched = true;
      });
      status_form.controls["name"].valueChanges.subscribe((name: string) => {
        if (unplannedTouched) return;
        const shouldBe = /^[12]\./.test((name ?? "").trim());
        if (unplannedCtrl.value !== shouldBe) {
          unplannedCtrl.setValue(shouldBe, { emitEvent: false });
        }
      });
    }

    return status_form;
  }

  openAddPriorityForm(): void {
    this.addPriorityForm = this.getPriorityForm();
  }

  openEditPriorityForm(priority: FullPriorityHash): void {
    this.editPriorityForm = this.getPriorityForm(priority);
  }

  getPriorityForm(template?: FullPriorityHash): UntypedFormGroup {
    const priority_form = new UntypedFormGroup({
      id: new UntypedFormControl({ value: "", disabled: true }),
      name: new UntypedFormControl({ value: "" }, Validators.required),
      color: new UntypedFormControl({ value: "" }, Validators.required),
      material_return: new UntypedFormControl({ value: false }),
      material_shipping: new UntypedFormControl({ value: false }),
      staging: new UntypedFormControl({ value: false }),
      days: new UntypedFormControl({ value: 0 }),
      hours: new UntypedFormControl({ value: 0 }),
      priority_idx: new UntypedFormControl({ value: 0 }),
      sla_type: new UntypedFormControl({ value: "NO" }),
      telephone_dispatching: new UntypedFormControl({ value: false }),
      project_id: new UntypedFormControl({ value: this.project.id }, Validators.required),
    });

    priority_form.controls["id"].setValue(template?.id || null);
    priority_form.controls["name"].setValue(template?.name || "");
    priority_form.controls["color"].setValue(template?.color || "#ff0000");
    priority_form.controls["material_return"].setValue(template ? template.material_return : false);
    priority_form.controls["material_shipping"].setValue(template ? template.material_shipping : false);
    priority_form.controls["staging"].setValue(template ? template.staging : false);
    priority_form.controls["days"].setValue(template?.days || 0);
    priority_form.controls["hours"].setValue(template?.hours || 0);
    priority_form.controls["priority_idx"].setValue(template?.priority_idx || 0);
    priority_form.controls["sla_type"].setValue(template ? template.sla_type : "NO");
    priority_form.controls["telephone_dispatching"].setValue(template ? template.telephone_dispatching : false);
    priority_form.controls["project_id"].setValue(this.project.id);

    priority_form.updateValueAndValidity();

    return priority_form;
  }

  onProjectDataChange(updatedProject: FullProjectHash): void {
    this.updateProjectData.emit(updatedProject);
  }

  deleteStatus(status: FullStatusHash): void {
    if (confirm(`Soll der Status wirklich gelöscht werden?`)) {
      this.projectService.deleteProjectStatus(this.project.id, status.id).subscribe({
        next: (updatedProject: FullProjectHash) => {
          this.onProjectDataChange(updatedProject);
        },
        error: (error) => {
          console.error("Error deleting status:", error);
        },
      });
    }
  }

  /**
   * Sortiert die Prioritäten per Drag & Drop um.
   * Das Backend liefert Prioritäten nach `priority_idx` sortiert — beim Verschieben
   * wird der Index jeder Priorität neu durchnummeriert und per PATCH gespeichert,
   * sodass die Reihenfolge dauerhaft erhalten bleibt.
   */
  dropPriorities(event: CdkDragDrop<FullPriorityHash[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    const priorities = [...this.project.priorities];
    moveItemInArray(priorities, event.previousIndex, event.currentIndex);

    // Alle Prioritäten, deren priority_idx durch das Verschieben einen neuen Wert bekommt
    const changed = priorities
      .map((priority, index) => ({ priority, index }))
      .filter(({ priority, index }) => priority.priority_idx !== index)
      .map(({ priority, index }) => ({ ...priority, priority_idx: index }));

    // Optimistische Anzeige: neue Reihenfolge sofort übernehmen, ohne auf das Backend zu warten
    this.project = {
      ...this.project,
      priorities: priorities.map((priority) => changed.find((c) => c.id === priority.id) ?? priority),
    };

    if (!changed.length) return;

    // Geänderte Prioritäten nacheinander speichern; die letzte Antwort enthält alle Updates
    concat(
      ...changed.map((priority) => this.projectService.updateProjectPriority(this.project.id, priority.id, priority)),
    )
      .pipe(last())
      .subscribe({
        next: (updatedProject: FullProjectHash) => this.onProjectDataChange(updatedProject),
        error: (error) => {
          console.error("Error updating priority order:", error);
        },
      });
  }

  deletePriority(priority: FullPriorityHash): void {
    if (confirm(`Soll die Priorität wirklich gelöscht werden?`)) {
      this.projectService.deleteProjectPriority(this.project.id, priority.id).subscribe({
        next: (updatedProject: FullProjectHash) => {
          this.onProjectDataChange(updatedProject);
        },
        error: (error) => {
          console.error("Error deleting priority:", error);
        },
      });
    }
  }
}
